// src/pages/Station.jsx
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ResultEntryDialog from "../components/ResultEntryDialog";
import StationResultsModal from "../components/StationResultsModal";
import AddStationModal from "../components/AddStationModal";
import EditStationModal from "../components/EditStationModal";
import { Loader2, HelpCircle } from "lucide-react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { loadCachedTeamsAndStations } from "../utils/loadCachedData";
import { teilweiseAusgesetzt, ausgesetztText } from "../utils/ausgesetzt";
import mergeStationsWithResultsAndEntries from "../utils/mergeStationsWithResultsAndEntries";
import useMetaWatcher from "../hooks/useMetaWatcher";
import { teamKey, gehoertZuTeam } from "../utils/teamKey";

export default function Station() {
  const [stations, setStations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedStationResults, setSelectedStationResults] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editStation, setEditStation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningDialog, setIsOpeningDialog] = useState(false);
  const [suppressWatcher, setSuppressWatcher] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const role = localStorage.getItem("role");

  // Memo: Teams nach Kategorie vorfiltern
  const { adults, kids } = useMemo(() => {
    const allAdults = teams.filter((t) => t.event_type === "Erwachsene");
    const allKids = teams.filter((t) => t.event_type === "Kinder");
    return { adults: allAdults, kids: allKids };
  }, [teams]);

  // === Daten laden: Teams, Stationen, Ergebnisse ===
  const fetchData = useCallback(
    async (options = {}) => {
      try {
        if (options.reloadTeams || options.reloadStations) setIsLoading(true);

        const { teams, stations, results, fromCache } = await loadCachedTeamsAndStations(
          BACKEND_URL,
          navigate,
          options
        );

        if (process.env.NODE_ENV === "development") {
          console.log(
            fromCache
              ? "[Station] Daten aus Cache geladen"
              : "[Station] Daten frisch vom Backend geladen"
          );
        }

        const mergedStations = mergeStationsWithResultsAndEntries(
          stations,
          results?.stations || [],
          []
        );

        setTeams(teams);
        setStations(mergedStations);

        if (options.reloadTeams || options.reloadStations) {
          toast.info("Stationen aktualisiert", { toastId: "stationReload" });
          setSuppressWatcher(true);
          setTimeout(() => setSuppressWatcher(false), 4000);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Daten:", error);
        toast.error("Fehler beim Laden der Daten.");
      } finally {
        setIsLoading(false);
      }
    },
    [BACKEND_URL, navigate]
  );

  // === Nur Ergebnisse neu laden ===
  const reloadResults = useCallback(async () => {
    try {
      console.log("[Station] reloadResults() gestartet");
      const latest = await fetchWithAuth(`${BACKEND_URL}/results`, navigate);

      const resultsStations = Array.isArray(latest?.stations)
        ? latest.stations
        : [];
      const resultsTeams = Array.isArray(latest?.teams)
        ? latest.teams
        : [];

      // 🔄 Ergebnisse direkt in Cache schreiben (ersetzt alten Results-Cache)
      try {
        localStorage.setItem(
          "results",
          JSON.stringify({
            data: latest, // volle Struktur wie vom Backend
            _meta: Date.now(),
            timestamp: Date.now(),
          })
        );
        console.log("[Cache][UPDATE] Ergebnisse im Cache aktualisiert");
      } catch (err) {
        console.warn("[Cache][UPDATE] Fehler beim Schreiben:", err);
      }

      // 🔁 Stationen aktualisieren
      setStations((prev) =>
        mergeStationsWithResultsAndEntries(prev, resultsStations, [])
      );

      // 🔁 Teams mit neuen Punkten aktualisieren
      setTeams((prevTeams) =>
        prevTeams.map((team) => {
          // Zuordnung ueber die stabile Team-ID – ein umbenanntes Team behaelt
          // dadurch seine Punkte, statt auf 0 zurueckzufallen.
          const result = resultsTeams.find(
            (r) => (r.team_id ?? r.team_name) === teamKey(team)
          );
          return result
            ? {
                ...team,
                total_points: result.total_points ?? 0,
                stations_completed: result.stations_completed ?? 0,
                age_bonus: result.age_bonus ?? 0,
              }
            : team;
        })
      );

      console.log("[Station] Ergebnisse neu geladen & gemerged");
      setSuppressWatcher(true);
      setTimeout(() => setSuppressWatcher(false), 3000);
    } catch (err) {
      console.error("Fehler beim Neu-Laden der Ergebnisse:", err);
      toast.error("Fehler beim Neu-Laden der Ergebnisse.");
    }
  }, [BACKEND_URL, navigate]);

  // === Nur Teams neu laden, still (ohne Ladeanzeige und Toast) ===
  // Fuer den offenen Eingabedialog: eine Nachmeldung oder Umbenennung soll
  // dort ankommen, ohne die Seite dahinter neu aufzubauen. Gibt die frische
  // Liste zurueck (der QR-Scan sucht darin erneut).
  const teamsNachladen = useCallback(async () => {
    const { teams: frisch } = await loadCachedTeamsAndStations(BACKEND_URL, navigate, {
      reloadTeams: true,
    });
    // Der Lader faengt Fehler selbst ab und liefert dann eine leere Liste –
    // die alte Liste bleibt in dem Fall stehen, der Aufrufer erfaehrt es.
    if (!Array.isArray(frisch) || frisch.length === 0) {
      throw new Error("Team-Liste konnte nicht geladen werden");
    }
    setTeams(frisch);
    return frisch;
  }, [BACKEND_URL, navigate]);

  // Aenderungen, die gemeldet wurden, waehrend sie nicht angewendet werden
  // konnten (Dialog offen bzw. kurz nach dem eigenen Neuladen).
  //
  // BEHOBENER FEHLER: Solche Meldungen wurden einfach verworfen. Der
  // MetaWatcher meldet jede Aenderung aber nur EINMAL – ein nachgemeldetes
  // oder umbenanntes Team kam deshalb nie im Dialog an, und der Betreuer hatte
  // den Dialog meist den ganzen Tag offen. Auch der QR-Scan meldete dann
  // "passt zu keinem bekannten Team".
  const ausstehendRef = useRef({ teams: false, stations: false, results: false });

  const aenderungenAnwenden = useCallback(
    ({ teams, stations, results }) => {
      if (teams || stations) {
        fetchData({ reloadTeams: teams, reloadStations: stations });
      } else if (results) {
        reloadResults();
      }
    },
    [fetchData, reloadResults]
  );

  // === MetaWatcher reagiert nur auf echte Änderungen ===
  useMetaWatcher(BACKEND_URL, navigate, ({ teams, stations, results }) => {
    if (selectedStation) {
      // Teams sofort in den offenen Dialog – er vertraegt eine neue Liste,
      // ohne die laufende Eingabe zu verlieren. Stationen und Ergebnisse
      // erst nach dem Schliessen, sonst baut sich die Seite dahinter um.
      if (teams) {
        teamsNachladen().catch((err) => {
          console.warn("[Station] Teams konnten nicht nachgeladen werden:", err);
          // Nicht verlieren: nach dem Schliessen erneut versuchen.
          ausstehendRef.current = { ...ausstehendRef.current, teams: true };
        });
      }
      const a = ausstehendRef.current;
      ausstehendRef.current = {
        teams: a.teams,
        stations: a.stations || stations,
        results: a.results || results,
      };
      return;
    }
    if (suppressWatcher) {
      const a = ausstehendRef.current;
      ausstehendRef.current = {
        teams: a.teams || teams,
        stations: a.stations || stations,
        results: a.results || results,
      };
      return;
    }
    aenderungenAnwenden({ teams, stations, results });
  });

  // Gemerkte Aenderungen nachholen, sobald der Dialog zu und die Sperre nach
  // dem eigenen Neuladen vorbei ist.
  useEffect(() => {
    if (selectedStation || suppressWatcher) return;
    const a = ausstehendRef.current;
    if (!a.teams && !a.stations && !a.results) return;
    ausstehendRef.current = { teams: false, stations: false, results: false };
    aenderungenAnwenden(a);
  }, [selectedStation, suppressWatcher, aenderungenAnwenden]);

  // === Initial Load ===
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === Modal & Button Handler ===
  const handleStationClick = (station) => setSelectedStationResults(station);
  const handleOpenResult = (station) => {
    setIsOpeningDialog(true);
    setSelectedStation(station);
    setTimeout(() => setIsOpeningDialog(false), 400);
  };
  const handleOpenEdit = (station) => setEditStation(station);
  const handleCloseDialog = () => setSelectedStation(null);

  const handleReloadAfterChange = useCallback(() => {
    fetchData({ reloadStations: true });
  }, [fetchData]);

  // === Skeleton ===
  const SkeletonCard = () => (
    <div className="w-full p-4 rounded-xl bg-secondary-800 shadow-md animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-secondary-700" />
        <div className="flex-1 space-y-3">
          <div className="h-5 bg-secondary-700 rounded w-2/3" />
          <div className="space-y-2">
            <div className="h-3 bg-secondary-700 rounded w-full" />
            <div className="h-3 bg-secondary-700 rounded w-5/6" />
          </div>
        </div>
      </div>
    </div>
  );

  // === Render ===
  return (
    <div className="p-4 bg-secondary-950 text-gray-200 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-200">Stationen</h1>
        {role === "admin" && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 rounded-lg bg-yellow-400 text-black font-medium hover:bg-yellow-300"
          >
            + Neu
          </button>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6 w-full">
          {Array.from({ length: 6 }).map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6 w-full">
          {stations.length === 0 ? (
            <div className="col-span-full text-gray-500 text-center">
              Keine Stationen vorhanden.
            </div>
          ) : (
            stations
              .slice()
              .sort((a, b) => a.station_number - b.station_number)
              .map((station) => {
                const results = Array.isArray(station.results)
                  ? station.results
                  : [];

                const adultsDone = adults.filter((t) =>
                  results.some((r) => gehoertZuTeam(r, t))
                ).length;
                const kidsDone = kids.filter((t) =>
                  results.some((r) => gehoertZuTeam(r, t))
                ).length;

                const adultsPct =
                  adults.length > 0
                    ? Math.round((adultsDone / adults.length) * 100)
                    : 0;
                const kidsPct =
                  kids.length > 0
                    ? Math.round((kidsDone / kids.length) * 100)
                    : 0;

                const isStationLoading =
                  isOpeningDialog &&
                  selectedStation?.station_number === station.station_number;

                return (
                  <div
                    key={station.station_number}
                    className={`cursor-pointer w-full p-4 rounded-xl bg-secondary-800 shadow-md hover:bg-secondary-700 transition-colors relative ${
                      teilweiseAusgesetzt(station) ? "ring-2 ring-red-500" : ""
                    }`}
                    onClick={() => handleStationClick(station)}
                  >
                    <div className="flex flex-col h-full">
                      <div className="mb-3">
                        <span className="text-xs font-bold text-yellow-400">
                          Station {station.station_number}
                        </span>
                        {teilweiseAusgesetzt(station) && (
                          <span className="ml-2 rounded bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                            ⏸ AUSGESETZT: {ausgesetztText(station)}
                          </span>
                        )}
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-200 break-words">
                          {station.station_name}
                        </h2>
                      </div>

                      {/* Fortschritt */}
                      <div className="flex-1 space-y-3">
                        {station.allow_groups !== "Kinder" && (
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-400 mb-1">
                              Erwachsene: {adultsDone}/{adults.length} ({adultsPct}%)
                            </p>
                            <div className="w-full bg-secondary-700 rounded-full h-3">
                              <div
                                className="bg-blue-500 h-3 rounded-full"
                                style={{ width: `${adultsPct}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {station.allow_groups !== "Erwachsene" && (
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-400 mb-1">
                              Kinder: {kidsDone}/{kids.length} ({kidsPct}%)
                            </p>
                            <div className="w-full bg-secondary-700 rounded-full h-3">
                              <div
                                className="bg-green-500 h-3 rounded-full"
                                style={{ width: `${kidsPct}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Buttons */}
                      <div className="flex justify-end space-x-2 mt-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(station);
                          }}
                          className="px-3 py-2 rounded-lg bg-secondary-700 text-gray-200 hover:bg-secondary-600 text-sm font-medium"
                        >
                          Details
                        </button>

                        {/* Quiz: das Ergebnis kommt vom Tablet. Betreuer tragen
                            dort nichts ein (das Backend lehnt es ohnehin ab);
                            der Admin pflegt Fragen und Versuche und kann im
                            Notfall von Hand korrigieren. */}
                        {station.station_type === "quiz" && role === "admin" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/quiz-verwaltung?station=${station.station_number}`);
                            }}
                            className="px-3 py-2 rounded-lg bg-secondary-700 text-gray-200 hover:bg-secondary-600 text-sm font-medium flex items-center gap-1"
                          >
                            <HelpCircle size={16} /> Quiz
                          </button>
                        )}
                        {station.station_type === "quiz" && role !== "admin" && (
                          <span className="px-3 py-2 text-sm text-gray-400">Ergebnis kommt vom Quiz-Tablet</span>
                        )}

                        {(role === "admin" || (role === "betreuer" && station.station_type !== "quiz")) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenResult(station);
                            }}
                            disabled={isStationLoading}
                            className="px-3 py-2 rounded-lg bg-yellow-400 text-gray-900 hover:bg-yellow-300 text-sm font-medium flex items-center justify-center"
                          >
                            {isStationLoading ? (
                              <Loader2 className="animate-spin w-4 h-4" />
                            ) : (
                              "Ergebnis eintragen"
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}

      {/* Modals */}
      <ResultEntryDialog
        open={!!selectedStation}
        onClose={handleCloseDialog}
        station={selectedStation}
        teams={teams}
        onSaved={reloadResults}
        onTeamsNeuLaden={teamsNachladen}
      />

      <AddStationModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSaved={handleReloadAfterChange}
      />

      <EditStationModal
        open={!!editStation}
        onClose={() => setEditStation(null)}
        station={editStation}
        onSaved={handleReloadAfterChange}
      />

      <StationResultsModal
        open={!!selectedStationResults}
        onClose={() => setSelectedStationResults(null)}
        station={selectedStationResults}
        teams={teams}
      />
    </div>
  );
}
